import { useEffect, useState } from 'react';
import { gnssState, gnssLive, getGnssCfg, subscribeGnss, poleOff } from './gnss';

// height above sea level: GNSS receiver > smoothed phone GPS > terrain model (Open-Meteo DEM, online)
const DEM_KEY = 'giss_dem_cache';
const demCache = (() => { try { return JSON.parse(localStorage.getItem(DEM_KEY)) || {}; } catch { return {}; } })();
const ck = (lat, lon) => `${(+lat).toFixed(4)},${(+lon).toFixed(4)}`;

export async function fetchDem(lat, lon) {
  const k = ck(lat, lon);
  if (demCache[k] != null) return demCache[k];
  const r = await fetch(`https://api.open-meteo.com/v1/elevation?latitude=${(+lat).toFixed(5)}&longitude=${(+lon).toFixed(5)}`);
  if (!r.ok) throw new Error('dem');
  const v = (await r.json()).elevation?.[0];
  if (v == null || Number.isNaN(+v)) throw new Error('dem');
  demCache[k] = +v;
  const keys = Object.keys(demCache);
  if (keys.length > 3000) keys.slice(0, 500).forEach((x) => delete demCache[x]);
  try { localStorage.setItem(DEM_KEY, JSON.stringify(demCache)); } catch { /* full */ }
  return +v;
}

let st = { alt: null, src: null, acc: null, dem: null, gps: null, gpsAcc: null, lat: null, lon: null, n: 0 };
const subs = new Set();
const emit = (p) => { st = { ...st, ...p }; subs.forEach((f) => f(st)); };
const samples = [];
const median = (a) => { const s = [...a].sort((x, y) => x - y); return s.length ? s[Math.floor(s.length / 2)] : null; };
let watchId = null, lastDem = null, users = 0;

function best() {
  const g = gnssState();
  if (gnssLive(g) && g.alt != null) return { alt: g.alt - poleOff(), src: 'gnss', acc: g.vrms ?? null };
  if (st.gps != null) return { alt: st.gps, src: 'gps', acc: st.gpsAcc };
  if (st.dem != null) return { alt: st.dem, src: 'dem', acc: null };
  return { alt: null, src: null, acc: null };
}
const refresh = () => emit(best());

function onPos(p) {
  const { latitude: lat, longitude: lon, altitude, altitudeAccuracy } = p.coords;
  if (altitude != null && (altitudeAccuracy == null || altitudeAccuracy < 60)) {
    samples.push({ a: altitude, acc: altitudeAccuracy, lat, lon });
    if (samples.length > 15) samples.shift();
  }
  const near = samples.filter((s) => Math.hypot((s.lat - lat) * 111000, (s.lon - lon) * 75000) < 40);
  emit({ lat, lon, n: near.length, gps: near.length >= 3 ? median(near.map((s) => s.a)) : null, gpsAcc: near.length >= 3 ? median(near.map((s) => s.acc ?? 30)) : null });
  if (!lastDem || Math.hypot((lastDem[0] - lat) * 111000, (lastDem[1] - lon) * 75000) > 40) {
    lastDem = [lat, lon];
    fetchDem(lat, lon).then((dem) => { emit({ dem }); refresh(); }).catch(() => {});
  }
  refresh();
}

export function startAltitude() {
  users += 1;
  if (watchId == null && navigator.geolocation) watchId = navigator.geolocation.watchPosition(onPos, () => {}, { enableHighAccuracy: true, maximumAge: 2000, timeout: 20000 });
  const off = subscribeGnss(refresh);
  return () => { off(); users -= 1; if (!users && watchId != null) { navigator.geolocation.clearWatch(watchId); watchId = null; } };
}
export const altitudeState = () => st;

export function useAltitude() {
  const [s, setS] = useState(st);
  useEffect(() => { subs.add(setS); const stop = startAltitude(); return () => { subs.delete(setS); stop(); }; }, []);
  return s;
}

const near = (a, lat, lon, m) => a.lat != null && Math.hypot((a.lat - lat) * 111000, (a.lon - lon) * 75000) < m;
// best height for a point: live GNSS/GPS if standing at it, else terrain model at the point
export async function heightFor(lat, lon) {
  const b = best();
  if (b.alt != null && b.src !== 'dem' && near(st, lat, lon, 25)) return { v: +b.alt.toFixed(2), src: b.src, acc: b.acc };
  return { v: +(await fetchDem(lat, lon)).toFixed(1), src: 'dem', acc: null };
}
export const fmtAlt = (v) => (v == null ? '–' : `${(+v).toFixed(1).replace('.', ',')} m`);
